import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Eye, Lock, FileText, ChevronRight } from 'lucide-react';
import './PrivacyPolicy.css';

export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      {/* Ambient backgrounds */}
      <div className="policy-bg">
        <div className="policy-bg__orb policy-bg__orb--1" />
        <div className="policy-bg__orb policy-bg__orb--2" />
        <div className="policy-bg__grid" />
      </div>

      <div className="policy-container">
        {/* Breadcrumbs */}
        <div className="policy-breadcrumbs">
          <Link to="/">Home</Link>
          <ChevronRight size={12} />
          <span>Privacy Policy</span>
        </div>

        {/* Header */}
        <header className="policy-header">
          <span className="policy-eyebrow">Trust & Security</span>
          <h1 className="policy-title">Privacy <span className="policy-title__accent">Policy</span></h1>
          <p className="policy-subtitle">
            Effective Date: June 1, 2026. Your privacy and trust are paramount to Luminar Entertainments.
          </p>
        </header>

        {/* Key trust highlights */}
        <div className="policy-highlights">
          <div className="policy-card">
            <div className="policy-card__icon"><Shield size={24} /></div>
            <h3>Encryption First</h3>
            <p>All sensitive transactions are processed via 256-bit cryptographically secure gateways.</p>
          </div>
          <div className="policy-card">
            <div className="policy-card__icon"><Eye size={24} /></div>
            <h3>Zero Selling</h3>
            <p>We do not sell, rent, or trade your personal event-going data with any third parties.</p>
          </div>
          <div className="policy-card">
            <div className="policy-card__icon"><Lock size={24} /></div>
            <h3>Discretion Always</h3>
            <p>True privacy and reputation protection for both private families and global brands.</p>
          </div>
        </div>

        {/* Main layout */}
        <div className="policy-layout">
          {/* Sidebar Index */}
          <aside className="policy-sidebar">
            <div className="policy-sidebar__sticky">
              <h3>Table of Contents</h3>
              <nav>
                <a href="#collection">1. Data We Collect</a>
                <a href="#usage">2. How We Use Data</a>
                <a href="#sharing">3. Data Sharing & Security</a>
                <a href="#cookies">4. Cookies & Analytics</a>
                <a href="#rights">5. Your Legal Rights</a>
                <a href="#contact-info">6. Contact Data Protection</a>
              </nav>
            </div>
          </aside>

          {/* Policy content */}
          <article className="policy-content">
            <section id="collection">
              <h2>1. Data We Collect</h2>
              <p>
                Luminar Entertainments acts as a data controller for personal information gathered during the ticket purchase process and general site interaction. We gather the minimum amount of data required to complete secure bookings:
              </p>
              <ul>
                <li><strong>Identity Information:</strong> Full name, billing credentials, and company associations where applicable.</li>
                <li><strong>Contact Information:</strong> Phone number and email address (used strictly for distributing digital ticket PDFs and order receipts).</li>
                <li><strong>Transaction Parameters:</strong> Ticket selections, quantities, amounts, timestamps, and order reference codes.</li>
                <li><strong>Payment Data:</strong> All checkouts utilize secure, third-party payment gateways. We do not store or process raw credit cards, UPI credentials, or net banking logs on our servers.</li>
              </ul>
            </section>

            <section id="usage">
              <h2>2. How We Use Data</h2>
              <p>
                We handle your data strictly in accordance with global standards (such as GDPR) and local regulations (Information Technology Act, 2000). Your data serves the following distinct purposes:
              </p>
              <ol>
                <li>Fulfilling ticket booking transactions and compiling official PDF ticket confirmations.</li>
                <li>Providing real-time updates regarding event schedules, venue adjustments, or cancellation notifications.</li>
                <li>Conducting secure gateway verifications to prevent fraudulent transactions.</li>
                <li>Complying with administrative requirements for large crowd management, stadium safety, and municipal legal compliance.</li>
              </ol>
            </section>

            <section id="sharing">
              <h2>3. Data Sharing & Security</h2>
              <p>
                Luminar Entertainments maintains strict administrative and technological safeguards. We do not share your private data except in the following limited circumstances:
              </p>
              <p>
                We share transactional metadata with our authorized payment gateway partners to securely generate digital tokens and process financial checks. In addition, we may cooperate with government authorities when legally required under official warrants or municipal crowd safety protocols.
              </p>
              <blockquote>
                <FileText size={16} />
                <strong>Discretion Protocol:</strong> Customer lists are stored in highly encrypted databases behind restrictive firewalls. All administrative personnel accessing logs must pass multi-factor verification checks.
              </blockquote>
            </section>

            <section id="cookies">
              <h2>4. Cookies & Analytics</h2>
              <p>
                We use highly lightweight browser cookies and session storage metrics (such as <code>luminar_loaded</code>) to:
              </p>
              <ul>
                <li>Maintain premium UI/UX loading transitions exactly once per session.</li>
                <li>Preserve ticket selection quantities inside checkout forms during checkout steps.</li>
                <li>Analyze site metrics like load times and responsive viewport behaviors to optimize layouts.</li>
              </ul>
              <p>
                You can block or disable cookies using standard browser settings. However, doing so may affect checkout features and custom transitions.
              </p>
            </section>

            <section id="rights">
              <h2>5. Your Legal Rights</h2>
              <p>
                Regardless of where you reside, you possess high-level control over your personal data:
              </p>
              <ul>
                <li><strong>Right of Access:</strong> You can request a digital file of all tickets and contact metrics registered under your email address.</li>
                <li><strong>Right of Rectification:</strong> You may correct any typographic errors in your transaction logs (like spelling mistakes in your name or phone number) by reaching our support lead.</li>
                <li><strong>Right of Deletion:</strong> You may request complete erasure of your transaction histories from our marketing databases after the corresponding event concludes.</li>
              </ul>
            </section>

            <section id="contact-info">
              <h2>6. Contact Data Protection</h2>
              <p>
                For any data questions, request submissions, or to contact our Chief Information Security officer, please reach out via:
              </p>
              <div className="policy-contact-details">
                <p><strong>Email:</strong> <a href="mailto:luminarentertainments123@gmail.com">luminarentertainments123@gmail.com</a></p>
                <p><strong>Hotline:</strong> +91 81290 77431</p>
                <p><strong>Office:</strong> Luminar entertainments private limited, 3rd Floor, Royal Plaza, Bank junction, Periyar Nagar, Aluva, Kerala 683101</p>
              </div>
            </section>
          </article>
        </div>
      </div>
    </div>
  );
}
